import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Typography,
  useTheme,
  Autocomplete,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LinkIcon from "@mui/icons-material/Link";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  AssociatedCampaignSync,
  useCreateAssociatedCampaignSyncMutation,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useGetUserDbCampaignsQuery,
  useGetCampaignByIdQuery,
} from "../../store/apis/campaignApi";
import { useGetInsertionOrdersByCampaignQuery } from "../../store/apis/insertionOrderApi";

interface AssociatedCampaignSyncFormProps {
  briefingId: string;
  associatedId: string;
  initialSync: AssociatedCampaignSync | null;
  platform: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FormState {
  beapoloCampaignId: string | null;
  internalCampaignId: string | null;
  insertionOrderId: string | null;
  isLinked: boolean;
}

const defaultState: FormState = {
  beapoloCampaignId: null,
  internalCampaignId: null,
  insertionOrderId: null,
  isLinked: false,
};

const DV360_PLATFORMS = [
  "BEAPOLO_DISNEY_PLUS",
  "BEAPOLO_MOVISTAR",
  "BEAPOLO_NETFLIX",
  "BEAPOLO_OTT",
  "BEAPOLO_SAMSUNG_TV",
  "BEAPOLO_YOUTUBE_CTV",
  "DV360",
];

const isDv360Platform = (platform: string): boolean => {
  return DV360_PLATFORMS.includes(platform);
};

const AssociatedCampaignSyncForm: React.FC<AssociatedCampaignSyncFormProps> = ({
  briefingId,
  associatedId,
  initialSync,
  platform,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [createSync, { isLoading }] = useCreateAssociatedCampaignSyncMutation();
  const { data: campaignsData, isLoading: isLoadingCampaigns } =
    useGetUserDbCampaignsQuery();

  const [formState, setFormState] = useState<FormState>(defaultState);
  const isDv360 = useMemo(() => isDv360Platform(platform), [platform]);

  // Fetch data for the second dropdown controlled by the selected campaign
  const { data: insertionOrdersData, isLoading: isLoadingIOs } =
    useGetInsertionOrdersByCampaignQuery(formState.beapoloCampaignId ?? "", {
      skip: !isDv360 || !formState.beapoloCampaignId,
    });

  const { data: campaignDetailsData, isLoading: isLoadingDetails } =
    useGetCampaignByIdQuery(
      { campaignId: formState.beapoloCampaignId ?? "" },
      { skip: isDv360 || !formState.beapoloCampaignId }
    );

  const internalCampaigns = useMemo(() => {
    return campaignDetailsData?.data?.internalCampaigns || [];
  }, [campaignDetailsData]);

  const insertionOrders = useMemo(() => {
    return insertionOrdersData?.data || [];
  }, [insertionOrdersData]);

  const campaigns = useMemo(() => {
    return campaignsData?.data || [];
  }, [campaignsData]);

  useEffect(() => {
    if (initialSync) {
      setFormState({
        beapoloCampaignId: initialSync.beapoloCampaignId || null,
        internalCampaignId: initialSync.internalCampaignId || null,
        insertionOrderId: initialSync.insertionOrderId || null,
        isLinked: initialSync.isLinked ?? false,
      });
    }
  }, [initialSync]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      await createSync({
        briefingId,
        associatedId,
        beapoloCampaignId: formState.beapoloCampaignId,
        internalCampaignId: formState.internalCampaignId,
        insertionOrderId: formState.insertionOrderId,
        isLinked: formState.isLinked,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t("briefingsEdit.syncUpdateSuccess", "Sync status updated"),
          severity: "success",
        })
      );
      onSuccess?.();
    } catch (error: unknown) {
      console.error("Failed to update sync", error);
      let msg = t(
        "briefingsEdit.syncUpdateError",
        "Failed to update sync status"
      );
      if (error && typeof error === "object" && "data" in error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errData = (error as any).data;
        if (errData) {
          if (Array.isArray(errData.errors) && errData.errors.length > 0) {
            msg = errData.errors
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((e: any) =>
                e.message
                  ? `${e.platform ? `[${e.platform}] ` : ""}${e.message}`
                  : JSON.stringify(e)
              )
              .join(", ");
          } else if (errData.message) {
            msg = errData.message;
          } else if (errData.error) {
            msg = errData.error;
          }
        }
      }
      dispatch(
        setToastMessage({
          message: msg,
          severity: "error",
        })
      );
    }
  };

  return (
    <Paper
      elevation={0}
      component="form"
      onSubmit={handleSubmit}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 4 },
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <LinkIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          {t("briefingsView.syncSectionTitle", "Linking status")}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Autocomplete<{ id: string; displayName?: string }>
          options={
            campaigns as unknown as { id: string; displayName: string }[]
          }
          getOptionLabel={(option) => option.displayName || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            campaigns.find((c) => c.id === formState.beapoloCampaignId) || null
          }
          onChange={(_, newValue) => {
            setFormState((prev) => ({
              ...prev,
              beapoloCampaignId: newValue?.id || null,
              internalCampaignId: null, // Reset dependent fields
              insertionOrderId: null,
            }));
          }}
          loading={isLoadingCampaigns}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("briefingsView.sync.beapoloCampaignId", "Campaign")}
              placeholder={t("common.select", "Select...")}
            />
          )}
        />

        {isDv360 ? (
          <Grid item xs={12}>
            <Autocomplete<{ id: string; displayName: string }>
              options={insertionOrders}
              getOptionLabel={(option) => option.displayName || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={
                insertionOrders.find(
                  (io) => io.id === formState.insertionOrderId
                ) || null
              }
              onChange={(_, newValue) => {
                setFormState((prev) => ({
                  ...prev,
                  insertionOrderId: newValue?.id || null,
                  internalCampaignId: null,
                }));
              }}
              disabled={!formState.beapoloCampaignId}
              loading={isLoadingIOs}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "briefingsView.sync.insertionOrderId",
                    "Insertion Order"
                  )}
                  placeholder={t("common.select", "Select...")}
                />
              )}
            />
            {!formState.beapoloCampaignId && (
              <Typography variant="caption" color="text.secondary">
                {t(
                  "briefingsView.sync.selectCampaignFirst",
                  "Select a campaign first"
                )}
              </Typography>
            )}
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Autocomplete
              options={
                internalCampaigns as unknown as {
                  id: string;
                  displayName: string;
                }[]
              }
              getOptionLabel={(option) => option.displayName || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={
                internalCampaigns.find(
                  (ic) => ic.id === formState.internalCampaignId
                ) || null
              }
              onChange={(_, newValue) => {
                setFormState((prev) => ({
                  ...prev,
                  internalCampaignId: newValue?.id || null,
                  insertionOrderId: null,
                }));
              }}
              disabled={!formState.beapoloCampaignId}
              loading={isLoadingDetails}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "briefingsView.sync.internalCampaignId",
                    "Internal Campaign"
                  )}
                  placeholder={t("common.select", "Select...")}
                />
              )}
            />
            {!formState.beapoloCampaignId && (
              <Typography variant="caption" color="text.secondary">
                {t(
                  "briefingsView.sync.selectCampaignFirst",
                  "Select a campaign first"
                )}
              </Typography>
            )}
          </Grid>
        )}

        <Grid item xs={12} display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={formState.isLinked}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    isLinked: e.target.checked,
                  }))
                }
                color="success"
              />
            }
            label={t("briefingsView.sync.isLinked", "Activate Sync Linking")}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isLoading}>
            {t("common.cancel", "Cancel")}
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("briefingsEdit.save", "Save Sync")
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default AssociatedCampaignSyncForm;
